import { ComponentProps, HTMLElementEvent } from "@ts/components";
import React, { FC, MouseEvent, useEffect, useState } from "react";

export type ImageZoomInputProps = ComponentProps & {
  src: string;
};

export type ImageZoomOutputProps = ImageZoomInputProps & {
  canZoom: boolean;
  height: number;
  onMouseMove: (e: MouseEvent<HTMLElement>) => void;
  width: number;
  x: number;
  y: number;
};

export const withImageZoom =
  (Component: FC<ImageZoomOutputProps>) =>
  ({ name = "ImageZoom", src, children, ratio }) => {
    const [[x, y], setXY] = useState([0, 0]);
    const [[width, height], setDimensions] = useState([0, 0]);
    const [canZoom, setCanZoom] = useState(true);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
      const {
        left,
        top,
        width: targetWidth,
        height: targetHeight,
      } = e.currentTarget.getBoundingClientRect();
      const [ratioNumerator, ratioDenominator] = ratio?.split("/") ?? [1, 1];
      const aspectRatioBox =
        parseInt(ratioNumerator, 10) / parseInt(ratioDenominator, 10);
      const aspectRatioImage = width / height;
      const aspectRatioDiff = (aspectRatioBox + aspectRatioImage) / 2;

      const x2 = ((e.clientX - left) / (targetWidth * 2)) * 100;
      const y2 =
        ((e.clientY - top) / (targetHeight * 2)) *
        (100 * (1 / aspectRatioDiff));
      if (width < targetWidth || height < targetHeight) {
        setCanZoom(false);
      } else {
        setXY([x2, y2]);
      }
    };

    useEffect(() => {
      const image = new Image();
      image.src = src;
      image.addEventListener(
        "load",
        (e: HTMLElementEvent<HTMLImageElement>) => {
          const { naturalWidth, naturalHeight } = e.target;
          setDimensions([naturalWidth, naturalHeight]);
        },
      );
    }, [src]);

    Component.displayName = name;
    return (
      <Component
        canZoom={canZoom}
        onMouseMove={handleMouseMove}
        src={src}
        x={x}
        y={y}
        width={width}
        height={height}
      >
        {children}
      </Component>
    );
  };
